import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { SurveyTemplate, SurveyRespondent } from '@/types/database';
import { Loader2, Mail, AlertCircle, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: SurveyTemplate[];
  respondents: SurveyRespondent[];
  onSend: (params: {
    name: string;
    description: string;
    surveyIds: string[];
    respondentIds: string[];
  }) => Promise<void>;
}

type Step = 'details' | 'surveys' | 'respondents' | 'review';
const STEPS: Step[] = ['details', 'surveys', 'respondents', 'review'];

export function CreateCampaignModal({
  isOpen,
  onClose,
  templates,
  respondents,
  onSend
}: CreateCampaignModalProps) {
  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);
  const [selectedRespondentIds, setSelectedRespondentIds] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  if (!isOpen) return null;

  const publishedTemplates = templates.filter(t => t.status === 'published');
  const validRespondents = respondents.filter(r => !!r.email);
  const stepIndex = STEPS.indexOf(step);

  const canAdvance = () => {
    switch (step) {
      case 'details': return name.trim().length > 0;
      case 'surveys': return selectedSurveyIds.length > 0;
      case 'respondents': return selectedRespondentIds.length > 0;
      default: return false;
    }
  };

  const goNext = () => {
    const next = STEPS[stepIndex + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const prev = STEPS[stepIndex - 1];
    if (prev) setStep(prev);
  };

  const toggleSurvey = (id: string) => {
    setSelectedSurveyIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleRespondent = (id: string) => {
    setSelectedRespondentIds(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const toggleAllRespondents = () => {
    if (selectedRespondentIds.length === validRespondents.length) {
      setSelectedRespondentIds([]);
    } else {
      setSelectedRespondentIds(validRespondents.map(r => r.id));
    }
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const updated = [...selectedSurveyIds];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, moved);
    setSelectedSurveyIds(updated);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleSubmit = async () => {
    setSending(true);
    setError(null);
    try {
      await onSend({
        name: name.trim(),
        description: description.trim(),
        surveyIds: selectedSurveyIds,
        respondentIds: selectedRespondentIds
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setStep('details');
    setName('');
    setDescription('');
    setSelectedSurveyIds([]);
    setSelectedRespondentIds([]);
    setError(null);
    onClose();
  };

  const selectedSurveys = selectedSurveyIds
    .map(id => publishedTemplates.find(t => t.id === id))
    .filter(Boolean) as SurveyTemplate[];

  const selectedRespondentData = validRespondents.filter(r =>
    selectedRespondentIds.includes(r.id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 max-h-[90vh] flex flex-col">
        <h2 className="text-xl font-semibold mb-1 text-gray-900 dark:text-white">
          Send Multiple Surveys
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Step {stepIndex + 1} of {STEPS.length}:{' '}
          {step === 'details' && 'Campaign Details'}
          {step === 'surveys' && 'Select Surveys'}
          {step === 'respondents' && 'Select Recipients'}
          {step === 'review' && 'Review & Send'}
        </p>

        {/* Progress bar */}
        <div className="flex gap-1 mb-6">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full ${
                i <= stepIndex
                  ? 'bg-blue-600 dark:bg-blue-500'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 1: Details */}
          {step === 'details' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., Q1 2026 Employee Assessment"
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Brief description of this campaign..."
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Step 2: Select Surveys */}
          {step === 'surveys' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select and order the surveys respondents will take. Drag to reorder.
              </p>

              {/* Selected surveys (ordered) */}
              {selectedSurveyIds.length > 0 && (
                <div className="space-y-1 mb-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Survey Order
                  </label>
                  {selectedSurveys.map((survey, index) => (
                    <div
                      key={survey.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={e => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg cursor-grab active:cursor-grabbing ${
                        dragIndex === index ? 'opacity-50' : ''
                      }`}
                    >
                      <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300 w-6">
                        {index + 1}.
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white truncate">
                        {survey.title}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                {publishedTemplates.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No published surveys available. Publish a survey first.
                  </p>
                ) : (
                  publishedTemplates.map(template => (
                    <label
                      key={template.id}
                      className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSurveyIds.includes(template.id)}
                        onChange={() => toggleSurvey(template.id)}
                        className="form-checkbox text-blue-600"
                      />
                      <div className="ml-3">
                        <span className="text-gray-900 dark:text-white text-sm">
                          {template.title}
                        </span>
                        {template.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                            {template.description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 3: Select Respondents */}
          {step === 'respondents' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {validRespondents.length} respondents with email addresses
                </p>
                <button
                  type="button"
                  onClick={toggleAllRespondents}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  {selectedRespondentIds.length === validRespondents.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-72 overflow-y-auto">
                {validRespondents.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No respondents with email addresses found.
                  </p>
                ) : (
                  validRespondents.map(respondent => (
                    <label
                      key={respondent.id}
                      className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedRespondentIds.includes(respondent.id)}
                        onChange={() => toggleRespondent(respondent.id)}
                        className="form-checkbox text-blue-600"
                      />
                      <span className="ml-3 text-gray-900 dark:text-white text-sm">
                        {respondent.first_name} {respondent.last_name}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        ({respondent.email})
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 'review' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Campaign</h3>
                <p className="text-gray-900 dark:text-white font-medium">{name}</p>
                {description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Surveys ({selectedSurveys.length})
                </h3>
                <ol className="list-decimal list-inside space-y-1">
                  {selectedSurveys.map(survey => (
                    <li key={survey.id} className="text-sm text-gray-900 dark:text-white">
                      {survey.title}
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Recipients ({selectedRespondentData.length})
                </h3>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {selectedRespondentData.map(r => (
                    <p key={r.id} className="text-sm text-gray-900 dark:text-white">
                      {r.first_name} {r.last_name}{' '}
                      <span className="text-gray-500 dark:text-gray-400">({r.email})</span>
                    </p>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Each recipient will receive <strong>1 email</strong> with a link to complete
                  all {selectedSurveys.length} surveys in sequence.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div>
            {stepIndex > 0 && (
              <Button variant="outline" onClick={goBack} disabled={sending}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={sending}>
              Cancel
            </Button>
            {step === 'review' ? (
              <Button onClick={handleSubmit} disabled={sending}>
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Campaign
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={goNext} disabled={!canAdvance()}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
